import React, { useEffect, useState } from 'react'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import { AppState } from 'redux/config/store'
import { useSelector } from 'react-redux'
import ProgramSelectItem from 'features/Programs/Launch/Components/ProgramSelectItem'
import launchProgramsWordings from '../Shared/wordings.json'
import styled from 'styled-components'
import { ProgramsType } from '../Shared/types'
import { launchProgramsActions } from './launchProgramActions'
import { programSuccessActions } from '../Success/ProgramSuccessActions'
import { useDispatch } from 'react-redux'
import {
  createAllSegmentsString,
  programsSelection,
  getCreatedProgramsForSegments,
  canCreateQtfForCurrentDate,
} from '../Shared/helperFunctions'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import { useNavigate } from 'react-router-dom'
import { Locations } from 'shared/Router/Locations'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import { Flex, Micromobility, QTF } from 'core-system/Icons/Sidebar'
import { Car } from 'core-system/Icons/modeTypes'
import CreditCard from 'core-system/Icons/Accounts/CreditCard'
import Coupon from 'core-system/Icons/Programs/Coupon'
import Insights from 'core-system/Icons/Sidebar/Insights'
import AlertSuccessModal from 'core-system/Modal/AlertSuccessModal'
import CancelIcon from '@mui/icons-material/Cancel'
import { programEditingActions } from '../Editing/ProgramEditingActions'
import camelize from 'camelize'
import WorksitesDateSelection from './Components/WorksitesDateSelection'
import CurrencySelector from './Components/CurrencySelector'

const TitleContainer = styled(Text)`
  margin-bottom: 1rem;
  color: ${palette.text.primary};
`

const PageItem = styled.div`
  margin-bottom: 2.5rem;
`

const IncompleteLaunchContainer = styled.div`
  margin-bottom: 3rem;
  background-color: ${palette.secondary.purple300};
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const TopModalButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
`

const LaunchProgramView = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { allSegments } = useSelector((state: AppState) => state.employer)
  const {
    selectedDate,
    selectedSegments,
    selectedPrograms,
    incompleteLaunch,
    draftState,
  } = useSelector((state: AppState) => state.launchPrograms)
  const allFlexPrograms = useSelector(
    (state: AppState) => state.flex.flexPrograms
  )
  const allGrhPrograms = useSelector((state: AppState) => state.grh.grhPrograms)
  const allQtfPrograms = useSelector((state: AppState) => state.qtf.qtfPrograms)
  const allMicromobilityPrograms = useSelector(
    (state: AppState) => state.micromobility.micromobilityPrograms
  )
  const [fetchingDraftState, setFetchingDraftState] = useState(true)
  const [alertModalState, setAlertModalState] = useState({
    open: false,
    type: 'alert' as 'alert' | 'success',
    headerText: '',
    message: '',
    buttonText: '',
    onButtonClick: () => {},
  })

  // Fetching any draft state
  useEffect(() => {
    fetchDraftProgramState()
  }, [])

  // Clean Selected Programs when ever this page is loaded
  // ! Important: otherwise the new cycle will have old selected programs
  useEffect(() => {
    dispatch(launchProgramsActions.clearStateForNewLaunch())
  }, [])

  const fetchDraftProgramState = async () => {
    // Disabling the launch program button, until any draft state is fetched
    setFetchingDraftState(true)
    await dispatch(launchProgramsActions.fetchDraftProgramState())
    setFetchingDraftState(false)
  }

  const handleProgramSelect = (id: ProgramsType) => {
    dispatch(
      launchProgramsActions.changeSelectedPrograms(
        programsSelection(id, selectedPrograms)
      )
    )
  }

  const isLaunchButtonDisabled =
    selectedSegments.length === 0 || selectedPrograms.length === 0

  const getProgramState = (id: ProgramsType) => {
    if (id === 'qtf') {
      return allQtfPrograms
    }
    if (id === 'grh') {
      return allGrhPrograms
    }
    if (id === 'micromobility') {
      return allMicromobilityPrograms
    }
    if (id === 'flex') {
      return allFlexPrograms
    }
    return null
  }

  const programsDisabled = (id: ProgramsType) => {
    if (selectedSegments.length === 0) {
      return {
        disabled: true,
        disabledReason: launchProgramsWordings.disabledReasons.selectGroup,
      }
    }
    if (!selectedDate) {
      return {
        disabled: true,
        disabledReason: launchProgramsWordings.disabledReasons.selectDate,
      }
    }
    // Transit Pass and Coupon have no per-group/tag uniqueness constraint
    if (id === 'transitPass' || id === 'coupon' || id === 'reportedUsage') {
      return {
        disabled: false,
        disabledReason: '',
      }
    }
    // Check can create QTF on selected date
    if (id === 'qtf') {
      if (!canCreateQtfForCurrentDate(selectedDate)) {
        return {
          disabled: true,
          disabledReason: launchProgramsWordings.disabledReasons.qtfDate,
        }
      }
    }
    const createdProgramsForSegments = getCreatedProgramsForSegments(
      selectedSegments,
      getProgramState(id)
    )
    const segmentsForPlaceHolder = createAllSegmentsString(
      selectedSegments,
      createdProgramsForSegments
    )
    if (createdProgramsForSegments.size > 0) {
      return {
        disabled: true,
        disabledReason: `${launchProgramsWordings.disabledReasons.programAlreadyCreated}: ${segmentsForPlaceHolder}`,
      }
    }
    return {
      disabled: false,
      disabledReason: '',
    }
  }

  const continueLaunch = async () => {
    let newStateJson = JSON.parse(draftState)
    newStateJson = camelize(newStateJson)
    await dispatch(launchProgramsActions.applyRehydrateState(newStateJson))
    await dispatch(programEditingActions.applyRehydrateState(newStateJson))
    dispatch(programSuccessActions.newEditCycleCleanup())
    navigate(Locations.Programs.Edit)
  }

  const discardPreviousStateAndLaunchNew = async () => {
    await dispatch(launchProgramsActions.deleteDraftProgramState())
    dispatch(programSuccessActions.newEditCycleCleanup())
    navigate(Locations.Programs.Edit)
  }

  const launchPrograms = () => {
    // If there is an incomplete launch, we need to show the alert success modal
    if (incompleteLaunch) {
      setAlertModalState({
        open: true,
        type: 'alert',
        headerText: launchProgramsWordings.alertSuccessModal.headerText,
        message: launchProgramsWordings.alertSuccessModal.message,
        buttonText: launchProgramsWordings.alertSuccessModal.buttonText,
        onButtonClick: discardPreviousStateAndLaunchNew,
      })
    } else {
      // Clearing the last launched programs
      dispatch(programEditingActions.clearState())
      dispatch(programSuccessActions.newEditCycleCleanup())
      navigate(Locations.Programs.Edit)
    }
  }

  const discardDraftState = async () => {
    try {
      await dispatch(launchProgramsActions.deleteDraftProgramState())
      setAlertModalState({
        open: true,
        type: 'success',
        headerText: 'Draft state discarded',
        message: '',
        buttonText: 'OK',
        onButtonClick: () =>
          setAlertModalState({ ...alertModalState, open: false }),
      })
    } catch (error) {
      setAlertModalState({
        open: true,
        type: 'alert',
        headerText: 'Error discarding draft state',
        message: 'Please try again',
        buttonText: 'OK',
        onButtonClick: () =>
          setAlertModalState({ ...alertModalState, open: false }),
      })
    }
  }

  return (
    <div>
      {incompleteLaunch && (
        <IncompleteLaunchContainer>
          <Text variant='action3'>
            {launchProgramsWordings.incompleteLaunch}
          </Text>
          <TopModalButtonsContainer>
            <SecondaryButton
              onClick={continueLaunch}
              endIcon={<CheckCircleIcon />}
            >
              Yes
            </SecondaryButton>
            <SecondaryButton
              onClick={discardDraftState}
              endIcon={<CancelIcon style={{ color: palette.error.red500 }} />}
            >
              Discard
            </SecondaryButton>
          </TopModalButtonsContainer>
        </IncompleteLaunchContainer>
      )}
      <WorksitesDateSelection
        allSegments={allSegments}
        selectedSegments={selectedSegments}
        selectedDate={selectedDate}
        isFromUpdate={false}
      />
      <CurrencySelector />
      <PageItem>
        <TitleContainer variant='h5'>
          {launchProgramsWordings.selectOptionsTitle}
        </TitleContainer>
        <ProgramSelectItem
          title={launchProgramsWordings.qtf.title}
          subtitle={launchProgramsWordings.qtf.subtitle}
          id='qtf'
          onSelect={handleProgramSelect}
          checked={selectedPrograms.includes('qtf')}
          disabled={programsDisabled('qtf').disabled}
          disabledReason={programsDisabled('qtf').disabledReason}
          icon={<QTF />}
        />
        <ProgramSelectItem
          title={launchProgramsWordings.grh.title}
          subtitle={launchProgramsWordings.grh.subtitle}
          id='grh'
          onSelect={handleProgramSelect}
          checked={selectedPrograms.includes('grh')}
          disabled={programsDisabled('grh').disabled}
          disabledReason={programsDisabled('grh').disabledReason}
          icon={<Car />}
        />
        <ProgramSelectItem
          title={launchProgramsWordings.micromobility.title}
          subtitle={launchProgramsWordings.micromobility.subtitle}
          id='micromobility'
          onSelect={handleProgramSelect}
          checked={selectedPrograms.includes('micromobility')}
          disabled={programsDisabled('micromobility').disabled}
          disabledReason={programsDisabled('micromobility').disabledReason}
          icon={<Micromobility />}
        />
        <ProgramSelectItem
          title={launchProgramsWordings.flex.title}
          subtitle={launchProgramsWordings.flex.subtitle}
          id='flex'
          onSelect={handleProgramSelect}
          checked={selectedPrograms.includes('flex')}
          disabled={programsDisabled('flex').disabled}
          disabledReason={programsDisabled('flex').disabledReason}
          icon={<Flex />}
        />
        <ProgramSelectItem
          title={launchProgramsWordings.transitPass.title}
          subtitle={launchProgramsWordings.transitPass.subtitle}
          id='transitPass'
          onSelect={handleProgramSelect}
          checked={selectedPrograms.includes('transitPass')}
          disabled={programsDisabled('transitPass').disabled}
          disabledReason={programsDisabled('transitPass').disabledReason}
          icon={<CreditCard />}
        />
        <ProgramSelectItem
          title={launchProgramsWordings.coupon.title}
          subtitle={launchProgramsWordings.coupon.subtitle}
          id='coupon'
          onSelect={handleProgramSelect}
          checked={selectedPrograms.includes('coupon')}
          disabled={programsDisabled('coupon').disabled}
          disabledReason={programsDisabled('coupon').disabledReason}
          icon={<Coupon />}
        />
        <ProgramSelectItem
          title={launchProgramsWordings.reportedUsage.title}
          subtitle={launchProgramsWordings.reportedUsage.subtitle}
          id='reportedUsage'
          onSelect={handleProgramSelect}
          checked={selectedPrograms.includes('reportedUsage')}
          disabled={programsDisabled('reportedUsage').disabled}
          disabledReason={programsDisabled('reportedUsage').disabledReason}
          icon={<Insights />}
        />
      </PageItem>
      <PageItem style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <PrimaryButton
          disabled={isLaunchButtonDisabled}
          onClick={launchPrograms}
          isLoading={fetchingDraftState}
        >
          Launch Programs
        </PrimaryButton>
      </PageItem>
      <AlertSuccessModal
        open={alertModalState.open}
        onClose={() => setAlertModalState({ ...alertModalState, open: false })}
        type={alertModalState.type}
        headerText={alertModalState.headerText}
        message={alertModalState.message}
        buttonText={alertModalState.buttonText}
        onButtonClick={() => {
          alertModalState.onButtonClick()
          setAlertModalState({ ...alertModalState, open: false })
        }}
      />
    </div>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  LaunchProgramView.displayName = 'LaunchProgramView'
}

export default React.memo(LaunchProgramView)
